import { useState } from 'react'
import { X, AlertTriangle } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import { useStore } from '../../store/useStore'
import { showToast } from '../../components/Toast'
import { ALLERGEN_INFO, DIETARY_INFO } from './constants'

interface DietaryModalProps {
  open: boolean
  onClose: () => void
}

export default function DietaryModal({ open, onClose }: DietaryModalProps) {
  const { t } = useI18n()
  // #2115 ゲスト (未ログイン) でも設定できる。user が無ければ端末に残した値を初期値にする
  const { user, setDietaryPreferences, guestDietaryAllergens, guestDietaryStyles } = useStore()
  const [allergens, setAllergens] = useState<string[]>(user?.dietaryAllergens ?? guestDietaryAllergens)
  const [styles, setStyles] = useState<string[]>(user?.dietaryStyles ?? guestDietaryStyles)

  // Reset form on open transition (derive-state-from-props pattern, avoids setState-in-useEffect).
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setAllergens(user?.dietaryAllergens ?? guestDietaryAllergens)
      setStyles(user?.dietaryStyles ?? guestDietaryStyles)
    }
  }

  if (!open) return null

  const handleSave = () => {
    setDietaryPreferences(allergens, styles)
    showToast({ type: 'success', message: t('profile.dietarySaved') })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[85vh] overflow-y-auto animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-text-primary">
            {t('profile.dietarySettings')}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect">
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        <div className="mb-4 rounded-lg bg-yellow-50 border border-yellow-200 p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-yellow-600 shrink-0 mt-0.5" />
          <p className="font-body text-xs text-yellow-800 leading-relaxed">
            {t('profile.dietaryDisclaimer')}
          </p>
        </div>

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('profile.dietaryAllergens')}
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(ALLERGEN_INFO).map(([key, info]) => {
            const active = allergens.includes(key)
            return (
              <button
                key={key}
                onClick={() => setAllergens(active ? allergens.filter(a => a !== key) : [...allergens, key])}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  active ? 'bg-red-500 text-white' : 'bg-gray-100 text-text-secondary border border-gray-200'
                }`}
              >
                <span>{info.emoji}</span>
                <span>{t(info.label)}</span>
              </button>
            )
          })}
        </div>

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('profile.dietaryStyles')}
        </p>
        <div className="flex flex-wrap gap-2 mb-5">
          {Object.entries(DIETARY_INFO).map(([key, info]) => {
            const active = styles.includes(key)
            return (
              <button
                key={key}
                onClick={() => setStyles(active ? styles.filter(d => d !== key) : [...styles, key])}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  active ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-text-secondary border border-gray-200'
                }`}
              >
                <span>{info.emoji}</span>
                <span>{t(info.label)}</span>
              </button>
            )
          })}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-medium text-text-secondary"
          >
            {t('profile.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="flex-1 rounded-xl bg-navy py-3 text-sm font-bold text-white"
          >
            {t('profile.save')}
          </button>
        </div>
      </div>
    </div>
  )
}
